import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown, Zap, Rocket, Star, Crown, ArrowRight } from 'lucide-react';

const pricingPlans = [
  {
    id: 'starter',
    icon: Zap,
    name: 'Starter',
    setupFee: '$500',
    monthlyFee: '$97',
    tagline: 'Get online. Fast.',
    badge: null,
    formLink: 'https://tally.so/r/1AMRNM',
    features: [
      '1–3 section website',
      '48-hour delivery',
      'Mobile responsive',
      'Contact form',
      'Hosting included',
      'Ongoing maintenance',
    ],
  },
  {
    id: 'growth',
    icon: Rocket,
    name: 'Growth',
    setupFee: '$1,000',
    monthlyFee: '$197',
    tagline: 'For businesses ready to grow.',
    badge: 'POPULAR',
    highlighted: true,
    formLink: 'https://tally.so/r/Pdlrkd',
    features: [
      'Everything in Starter',
      'Up to 6 sections',
      'Booking system',
      'Monthly check-ins',
      'Priority support',
    ],
  },
  {
    id: 'pro',
    icon: Star,
    name: 'Pro',
    setupFee: '$2,500',
    monthlyFee: '$397',
    tagline: 'The unfair advantage.',
    badge: null,
    formLink: 'https://tally.so/r/pbGzvZ',
    features: [
      'Everything in Growth',
      'Up to 9 sections',
      'Advanced animations',
      'AI chat assistant',
      'Quarterly refresh',
      '24hr support',
    ],
  },
  {
    id: 'elite',
    icon: Crown,
    name: 'Elite',
    setupFee: 'Custom',
    monthlyFee: '$600+',
    tagline: 'We run everything.',
    badge: null,
    isCustom: true,
    features: [
      'Everything in Pro',
      'Unlimited sections',
      'Full brand system',
      'Direct line to Tyler',
      'White glove service',
    ],
  },
];

const PricingCard = ({ plan, isExpanded, onToggle, index }) => {
  const Icon = plan.icon;
  
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  // Varied animation timing
  const animDelay = [0, 0.08, 0.05, 0.12][index];
  const animDuration = [0.5, 0.55, 0.48, 0.52][index];
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 35 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: animDuration, delay: animDelay, ease: 'easeOut' }}
      className={`relative bg-[#111111] border transition-colors ${
        plan.highlighted 
          ? 'border-[#e62020] lg:-translate-y-2' 
          : 'border-[#1a1a1a] hover:border-[#262626]'
      }`}
      data-testid={`pricing-card-${plan.id}`}
    >
      {/* Badge */}
      {plan.badge && (
        <div className="absolute -top-3 left-6 px-3 py-1 bg-[#e62020] text-white text-[9px] font-semibold uppercase tracking-wider">
          {plan.badge}
        </div>
      )}

      <div className="p-6 md:p-7">
        {/* Header */}
        <div className="flex items-center gap-2.5 mb-5">
          <Icon size={18} className="text-[#e62020]" />
          <h3
            className="text-xl font-bold uppercase tracking-tight text-white"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
          >
            {plan.name}
          </h3>
        </div>

        {/* Pricing */}
        <div className="mb-5">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              {plan.setupFee}
            </span>
            <span className="text-[#888888] text-sm">setup</span>
          </div>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-[#e62020] text-lg font-semibold">+ {plan.monthlyFee}</span>
            <span className="text-[#888888] text-sm">/mo</span>
          </div>
        </div>

        {/* Tagline */}
        <p className="text-[#888888] text-sm mb-5">
          {plan.tagline}
        </p>

        {/* Features toggle */}
        <button
          onClick={onToggle}
          className="flex items-center justify-between w-full py-3 border-t border-[#1a1a1a] text-[#888888] hover:text-white transition-colors mb-5 group"
          data-testid={`pricing-expand-${plan.id}`}
        >
          <span className="text-xs uppercase tracking-wider">What's included</span>
          <motion.div 
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.25 }}
          >
            <ChevronDown size={14} className="group-hover:text-[#e62020] transition-colors" />
          </motion.div>
        </button>

        {/* Features */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <ul className="space-y-2.5 mb-5 pb-5 border-b border-[#1a1a1a]">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2.5">
                    <Check size={12} className="text-[#e62020] mt-1 flex-shrink-0" />
                    <span className="text-[#888888] text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>

        {/* CTA */}
        {plan.isCustom ? (
          <motion.button
            onClick={scrollToContact}
            className="w-full py-3.5 text-sm font-semibold uppercase tracking-wider bg-[#111111] border border-[#e62020] text-[#e62020] hover:bg-[#e62020] hover:text-white transition-colors flex items-center justify-center gap-2"
            data-testid={`pricing-cta-${plan.id}`}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            Let's talk <ArrowRight size={14} />
          </motion.button>
        ) : (
          <motion.a
            href={plan.formLink}
            target="_blank"
            rel="noopener noreferrer"
            className={`w-full py-3.5 text-sm font-semibold uppercase tracking-wider block text-center transition-colors flex items-center justify-center gap-2 ${
              plan.highlighted
                ? 'bg-[#e62020] text-white hover:bg-[#c0392b]'
                : 'border border-[#262626] text-white hover:border-[#e62020] hover:text-[#e62020]'
            }`}
            data-testid={`pricing-cta-${plan.id}`}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            Get started <ArrowRight size={14} />
          </motion.a>
        )}
      </div>
    </motion.div>
  );
};

export const PricingSection = () => {
  const [expandedCard, setExpandedCard] = useState('growth'); // Default open the popular one

  return (
    <section id="pricing" className="py-28 md:py-36 bg-[#0a0a0a] relative overflow-hidden" data-testid="pricing-section">
      {/* Background */}
      <div className="absolute inset-0 cyber-grid opacity-[0.03]" />
      
      <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="mb-14"
        >
          <p className="text-[#e62020] text-xs tracking-[0.2em] uppercase mb-5">
            Pricing
          </p>
          <h2
            className="text-4xl md:text-5xl lg:text-6xl font-bold uppercase leading-[0.95] text-white"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            data-testid="pricing-headline"
          >
            Simple pricing.
            <br />
            <span className="text-[#888888]">No surprises.</span>
          </h2>
          <p className="text-[#888888] text-sm mt-4 max-w-md">
            Ready to move fast? Pick a plan and fill out the form — we'll get started right away.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          {pricingPlans.map((plan, index) => (
            <PricingCard
              key={plan.id}
              plan={plan}
              index={index}
              isExpanded={expandedCard === plan.id}
              onToggle={() => setExpandedCard(expandedCard === plan.id ? null : plan.id)}
            />
          ))}
        </div>

        {/* Footnote */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="text-[#888888] text-sm"
        >
          Setup is one-time. Monthly covers hosting, maintenance, and support. Cancel anytime. 
          <span className="text-[#e62020]/80 ml-1">Prefer to chat first? Scroll down to the contact form.</span>
        </motion.p>
      </div>
    </section>
  );
};
